import { useTranslation } from '@rhino-project/core/i18n';
import { useCallback, useEffect } from 'react';

import { SubmitButton } from '../buttons';
import {
  useModelShow,
  useModelUpdate,
  useFieldSetErrors,
  useResolver,
  useSchema,
  useDefaultValues
} from '@rhino-project/core/hooks';
import { NetworkParamError } from '@rhino-project/core/lib';
import { useForm, type FieldValues } from 'react-hook-form';
import {
  type RhinoRecordIdentifier,
  useRhinoContext
} from '@rhino-project/core';
import { FormProvider } from '@rhino-project/core/components/forms';
import { FieldString } from '../../Field';
import {
  Alert,
  AlertContent,
  AlertTitle,
  Fieldset,
  FieldGroup,
  FieldsetActions,
  Form,
  toast
} from '@heroui/react';
import { FormErrors } from '../forms';

// Stable identity so useSchema's memo doesn't rebuild every render.
const PROFILE_FIELDS = ['name', 'nickname'];

export const EditProfile = () => {
  const { t } = useTranslation();
  // The account show endpoint resolves the current user with no explicit id;
  // pass null through as the (string | number) identifier the hook expects.
  const { model, resource: account } = useModelShow(
    'account',
    null as unknown as RhinoRecordIdentifier
  );
  const { mutate, isPending, error } = useModelUpdate(model);
  const paramError = error as NetworkParamError | null;
  const { refreshSession } = useRhinoContext();

  // Validation and defaults come from the model's generated schema, so nullable
  // fields (e.g. nickname) accept null/blank.
  const schema = useSchema(model, PROFILE_FIELDS);
  const defaultValues = useDefaultValues(model, PROFILE_FIELDS);
  const resolver = useResolver(schema);

  const methods = useForm<FieldValues>({
    defaultValues,
    disabled: isPending,
    values: account as FieldValues | undefined,
    mode: 'onBlur',
    resolver
  });
  const {
    handleSubmit,
    setError,
    setFocus,
    formState: { isDirty }
  } = methods;

  const onError = useFieldSetErrors(setError);
  const onSubmit = useCallback(
    // mutate's onError receives the network error at runtime, which carries the
    // `.errors` payload useFieldSetErrors reads; bridge the differing signatures.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (values: Record<string, any>) =>
      mutate(values, {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onError: onError as any,
        onSuccess: () => {
          toast.success(
            t(
              'rhino.frontend.messages.your_profile_has_been_updated_successfully'
            )
          );
          // Refresh the user shown in the account menu.
          refreshSession();
        }
      }),
    [mutate, onError, refreshSession, t]
  );

  useEffect(() => setFocus('name'), [setFocus]);

  return (
    <FormProvider {...methods}>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Fieldset>
          <FormErrors />
          <FieldGroup>
            <FieldString
              path="name"
              label={t('rhino.frontend.messages.name')}
            />
            <FieldString
              path="nickname"
              label={t('rhino.frontend.messages.nick_name')}
            />
          </FieldGroup>
          {Array.isArray(paramError?.errors) && (
            <Alert status="danger">
              <AlertContent>
                <AlertTitle>{paramError.errors[0]}</AlertTitle>
              </AlertContent>
            </Alert>
          )}
          <FieldsetActions>
            <SubmitButton isPending={isPending} isDisabled={!isDirty}>
              {t('rhino.frontend.messages.update_profile')}
            </SubmitButton>
          </FieldsetActions>
        </Fieldset>
      </Form>
    </FormProvider>
  );
};
